import { QueryHookOptions, useMutation } from '@apollo/client';
import { LANDING_PATH } from '../../constants';
import { GetSettings, SetSettings } from '../../graphql/queries/J1';
import useOptimisticQuery, {
  OptimisticQueryResults,
} from '../../hooks/graphql/useOptimisticQuery';

type SettingsUpdate<T> = (data: T | null) => T | null;

const useSettings = <T>(
  resourceType: 'user' | 'account',
  category: string, // path to fetch resource from (ie "landing/library/userSettings"),
  resourceId?: string,
  apolloSettings?: QueryHookOptions<any, any>
) => {
  // query settings
  const { getUpdater, ...settings } = useOptimisticQuery<T | null>(
    GetSettings,
    {
      variables: {
        resourceId,
        resourceType,
        category,
      },
      context: {
        mute: true,
      },
      dataPath: 'getSettings.data',
      ...apolloSettings,
    }
  );

  // mutate settings
  const [mutateSetSettings] = useMutation(SetSettings);
  const saveSettings = async (data: T | SettingsUpdate<T> | null) => {
    await mutateSetSettings({
      variables: { data, resourceType, category, resourceId },
      update: getUpdater(
        typeof data === 'function' ? (data as SettingsUpdate<T>) : () => data
      ),
      optimisticResponse: {
        __typename: 'Mutation',
        setSettings: {
          __typename: 'SetSettingsResult',
          resultCode: 'UPDATED',
        },
      },
    });
  };

  return [settings, saveSettings] as [
    Omit<OptimisticQueryResults<T | null>, 'getUpdater'>,
    typeof saveSettings
  ];
};

export default useSettings;

export function useDefaultAppSetting() {
  const [defaultAppResults, setDefaultApp] = useSettings<string>(
    'user',
    '/global/defaultApp'
  );
  const defaultAppPath = defaultAppResults.data || LANDING_PATH;

  return [defaultAppPath, setDefaultApp] as [string, typeof setDefaultApp];
}

export function useBulkUploadForPoliciesSetting() {
  const [
    bulkUploadForPolicies,
    setBulkUploadForPolicies,
  ] = useSettings<boolean>('account', '/global/bulkUploadForPolicies');

  return [bulkUploadForPolicies.data, setBulkUploadForPolicies] as [
    boolean | null,
    typeof setBulkUploadForPolicies
  ];
}
